import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, createWebSocketUrl } from '../utils/api';

interface Submission {
  id: string;
  ffId: string;
  screenshotUrl: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

export function StatusPage() {
  const { ffId } = useParams<{ ffId: string }>();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAnimation, setShowAnimation] = useState(false);
  const [animatingSubmission, setAnimatingSubmission] = useState<Submission | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!ffId) return;
    fetchSubmissions();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [ffId]);

  const fetchSubmissions = async () => {
    if (!ffId) return;
    try {
      setLoading(true);
      const data = await api.getSubmissions(ffId);
      setSubmissions(data.submissions);
      checkForNewApproval(data.submissions);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    if (!ffId) return;
    
    const wsUrl = createWebSocketUrl(ffId);
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('WebSocket connected');
      wsRef.current?.send(JSON.stringify({ type: 'subscribe', payload: { all: true } }));
    };

    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'submission_update' && message.payload) {
          handleSubmissionUpdate(message.payload);
        }
      } catch (e) {
        console.error('WS message parse error:', e);
      }
    };

    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected, reconnecting...');
      setTimeout(connectWebSocket, 3000);
    };

    wsRef.current.onerror = (err) => {
      console.error('WebSocket error:', err);
    };
  };

  const handleSubmissionUpdate = (updatedSubmission: Submission) => {
    setSubmissions(prev => {
      const existing = prev.find(s => s.id === updatedSubmission.id);
      if (existing && existing.status === 'pending' && updatedSubmission.status === 'approved') {
        setAnimatingSubmission(updatedSubmission);
        setShowAnimation(true);
      }
      return prev.map(s => s.id === updatedSubmission.id ? updatedSubmission : s);
    });
  };

  const checkForNewApproval = (newSubmissions: Submission[]) => {
    const latestApproved = newSubmissions.find(s => s.status === 'approved');
    if (latestApproved) {
      setAnimatingSubmission(latestApproved);
      setShowAnimation(true);
    }
  };

  const closeAnimation = () => {
    setShowAnimation(false);
    setAnimatingSubmission(null);
  };

  const getLatestSubmission = () => {
    return submissions[0];
  };

  const latest = getLatestSubmission();

  if (loading) {
    return (
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="spinner" style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  if (error && !latest) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ color: 'var(--error)', marginBottom: '1rem' }}>⚠️</div>
          <h2>Error loading status</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>{error}</p>
          <Link to="/" className="btn btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '1.75rem', fontWeight: '800', color: 'var(--text)' }}>Submission Status</h1>
              <p style={{ color: 'var(--text-muted)' }}>FF ID: <strong>{ffId}</strong></p>
            </div>
            <Link to="/" className="btn btn-secondary">New Submission</Link>
          </div>

          {latest ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <span className={`status-badge status-${latest.status}`}>
                  {latest.status.charAt(0).toUpperCase() + latest.status.slice(1)}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  Submitted: {new Date(latest.createdAt).toLocaleString()}
                </span>
              </div>

              {latest.screenshotUrl && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <img 
                    src={latest.screenshotUrl} 
                    alt="Submitted screenshot" 
                    className="preview-image"
                    style={{ border: '1px solid var(--border)' }}
                  />
                </div>
              )}

              {latest.status === 'pending' && (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--warning-light)', borderRadius: 'var(--radius)' }}>
                  <div className="spinner" style={{ margin: '0 auto 1rem' }} />
                  <p style={{ color: '#92400e', fontWeight: '500' }}>Your submission is under review</p>
                  <p style={{ color: '#92400e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    An admin will review your screenshot shortly. This page updates automatically.
                  </p>
                </div>
              )}

              {latest.status === 'approved' && (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--success-light)', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                  <h2 style={{ color: '#065f46', marginBottom: '0.5rem' }}>Approved!</h2>
                  <p style={{ color: '#065f46' }}>Your unban request has been approved. Animation will start in-game.</p>
                  {latest.approvedAt && (
                    <p style={{ color: '#065f46', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      Approved at: {new Date(latest.approvedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {latest.status === 'rejected' && (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--error-light)', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                  <h2 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>Rejected</h2>
                  <p style={{ color: '#991b1b' }}>Your submission was not approved.</p>
                  {latest.rejectionReason && (
                    <p style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      Reason: {latest.rejectionReason}
                    </p>
                  )}
                  {latest.rejectedAt && (
                    <p style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      Rejected at: {new Date(latest.rejectedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <p>No submissions found for this FF ID</p>
              <Link to="/" className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
                Submit Screenshot
              </Link>
            </div>
          )}
        </div>
      </div>

      {showAnimation && animatingSubmission && (
        <div className="success-animation" onClick={closeAnimation}>
          <div className="success-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text)', marginBottom: '0.5rem' }}>
              Approved! 🎉
            </h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
              Your unban request has been approved. The animation will now start in-game.
            </p>
            <button onClick={closeAnimation} className="btn btn-success">
              Continue
            </button>
          </div>
        </div>
      )}
    </>
  );
}